<template>
  <!-- Disconnect Dialog -->
  <DeleteConfirmDialog
    v-model:visible="disconnectDialogVisibility"
    :loading="deleteLoading"
    :title="i18n.t('disconnectDataSource')"
    :sub-title="
      i18n.t('disconnectDataSourceDescription', { name: dataSource.name })
    "
    @cancel="disconnectDialogVisibility = false"
    @delete="handleDisconnect" />

  <!-- Main Dialog -->
  <SmDialog
    v-model:visibility="visibility"
    :title="props.dataSource.name"
    size="medium">
    <!-- Header with Disconnect Button -->
    <template #close>
      <div class="flex items-center gap-2">
        <Trash
          class="cursor-pointer hover:text-magenta"
          :size="20"
          @click="handleDisconnectClick" />
        <SmButtonClose @close="visibility = false" />
      </div>
    </template>

    <!-- Tabs -->
    <el-tabs v-model="activeTab">
      <!-- General Tab -->
      <el-tab-pane label="General" name="general">
        <el-form
          ref="formRef"
          :model="formData"
          :rules="formRules"
          label-position="top"
          require-asterisk-position="right">
          <el-form-item prop="name" label="Name:">
            <el-input v-model="formData.name" />
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <!-- Extension Attributes Tab (Microsoft only) -->
      <el-tab-pane
        v-if="isMicrosoftDataSource"
        label="Extension Attributes"
        name="extension-attributes"
        lazy>
        <ExtensionAttributesTab v-model="extensionAttributeMappings" />
      </el-tab-pane>
    </el-tabs>

    <!-- Footer -->
    <template #footer>
      <div class="flex justify-end gap-4">
        <SmButton outline @click="handleCancel">
          {{ i18n.t('general.cancel') }}
        </SmButton>
        <SmButton
          :loading="saveLoading"
          data-tutorial-id="application-create-button"
          @click="handleSave">
          {{ i18n.t('general.save') }}
        </SmButton>
      </div>
    </template>
  </SmDialog>
</template>

<script setup lang="ts">
  import {
    DataSource,
    DataSourcesService,
    DataSourceIn,
    type DataSourceConfigOptional,
    type ExtensionAttributeMappingIn,
  } from '@/client'
  import { useI18n } from 'vue-i18n'
  import { ref, computed, watch } from 'vue'
  import { Trash } from 'lucide-vue-next'
  import { useRouter } from 'vue-router'
  import DeleteConfirmDialog from '@/components/shared/DeleteConfirmDialog.vue'
  import ExtensionAttributesTab from './ExtensionAttributesTab.vue'

  const props = defineProps<{ dataSource: DataSource }>()
  const emit = defineEmits<{ refetchDataSource: [] }>()

  const i18n = useI18n()
  const router = useRouter()
  const disconnectDialogVisibility = ref(false)
  const deleteLoading = ref(false)
  const visibility = defineModel<boolean>('visibility')
  const saveLoading = ref(false)
  type ExtensionAttributeField = ExtensionAttributeMappingIn[string]

  // Tab management
  const activeTab = ref<'general' | 'extension-attributes'>('general')

  // Microsoft datasource detection
  const isMicrosoftDataSource = computed(() => {
    const microsoftIntegrations = ['microsoft']
    return microsoftIntegrations.some((id) =>
      props.dataSource.integration_id?.toLowerCase().includes(id)
    )
  })

  // Form data
  const formData = ref<DataSourceIn & { type: string }>({
    name: props.dataSource.name,
    tags: props.dataSource.tags,
    config: props.dataSource.config,
    type: 'token',
  })

  // Ensure extension_attribute_config exists with defaults
  formData.value.config = {
    ...formData.value.config,
    extension_attribute_config: formData.value.config
      ?.extension_attribute_config ?? { mappings: {} },
  }

  // Extension attributes mappings (array format for UI)
  const extensionAttributeMappings = ref<
    Array<{
      microsoftAttribute: string | null
      saasmetrixField: ExtensionAttributeField
    }>
  >([])

  // Update formData when dataSource prop changes
  watch(
    () => props.dataSource,
    (newDataSource) => {
      formData.value = {
        name: newDataSource.name,
        tags: newDataSource.tags,
        config: {
          ...newDataSource.config,
          extension_attribute_config: newDataSource.config
            ?.extension_attribute_config ?? { mappings: {} },
        },
        type: 'token',
      }
      // Reload mappings if dialog is open
      if (visibility.value) {
        loadMappings()
      }
    },
    { deep: true }
  )

  // Load existing mappings when dialog opens
  watch(visibility, (newVal) => {
    if (newVal) {
      loadMappings()
    }
  })

  function loadMappings() {
    const mappingsDict =
      formData.value.config?.extension_attribute_config?.mappings || {}

    extensionAttributeMappings.value = Object.entries(mappingsDict).map(
      ([microsoftAttr, saasmetrixField]) => ({
        microsoftAttribute: microsoftAttr,
        saasmetrixField: saasmetrixField as ExtensionAttributeField,
      })
    )
  }

  // Disconnect handler
  function handleDisconnectClick() {
    disconnectDialogVisibility.value = true
  }

  function handleDisconnect() {
    deleteLoading.value = true
    DataSourcesService.deleteDataSourceApiV1DataSourceDataSourcesDataSourceIdDelete(
      {
        dataSourceId: props.dataSource._id,
      }
    )
      .then(() => {
        disconnectDialogVisibility.value = false
        visibility.value = false
        router.push({ name: 'Data-Sources' })
      })
      .finally(() => {
        deleteLoading.value = false
      })
  }

  // Save handler
  function handleSave() {
    saveLoading.value = true

    // Convert mappings array to dict format
    const existingMappings =
      formData.value.config?.extension_attribute_config?.mappings || {}
    const mappingsDict: ExtensionAttributeMappingIn = {}

    Object.keys(existingMappings).forEach((mappingKey) => {
      mappingsDict[mappingKey] = null
    })

    extensionAttributeMappings.value.forEach((mapping) => {
      if (mapping.microsoftAttribute && mapping.saasmetrixField) {
        mappingsDict[mapping.microsoftAttribute] = mapping.saasmetrixField
      }
    })

    // Include extension attributes in config
    const configToUpdate: DataSourceConfigOptional = {
      ...formData.value.config,
      extension_attribute_config: {
        mappings: mappingsDict,
      },
    }

    DataSourcesService.putDataSourcesApiV1DataSourceDataSourcesDataSourceIdPut({
      dataSourceId: props.dataSource._id,
      requestBody: {
        ...formData.value,
        config: configToUpdate,
      },
    })
      .then(() => {
        visibility.value = false
        emit('refetchDataSource')
      })
      .finally(() => {
        saveLoading.value = false
      })
  }

  function handleCancel() {
    visibility.value = false
  }

  const formRules = {
    name: [
      { required: true, message: i18n.t('fieldIsRequired'), trigger: 'blur' },
    ],
  }
</script>
